import { ODataVisitor, RequestBuilder } from '@angry-yard/coleslaw';
import { ExpressionInterface, PropertyExpression, ValueExpression } from '@angry-yard/coleslaw/expressions';
import { Equals, GreaterThan, GreaterThanOrEqualTo, LessThan, LessThanOrEqualTo, Not, NotEquals } from '@angry-yard/coleslaw/filters';
import { Contains, EndsWith, StartsWith } from '@angry-yard/coleslaw/methods';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { FilterDescriptor, State, toODataString } from '@progress/kendo-data-query';
import { serializeFilter } from '@progress/kendo-data-query/dist/es/odata-filtering.operators';
import { Table } from 'app/shared/models';
import { ODataRequest } from './o-data-request';

@Injectable({
  providedIn: 'root'
})
export class RequestFactory {
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  private baseUrl: string;

  constructor() {
    this.baseUrl = this.environment.apiUrl;

    //override default behavior
    ODataVisitor.prototype['visitValueExpression'] = function (ex) {
      let result;
      let val = ex.value();
      if (val instanceof Date) {
        result = val.toISOString();
      } else if (typeof val === 'string') {
        let isRegex = val.match(/[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}/);
        result = isRegex ? "guid'" + ex.value().toString() + "'" : "'" + ex.value().toString() + "'";
      } else {
        result = val.toString();
      }
      this.items.push(result);
    };

    let isPresent = function (value) { return value !== null && value !== undefined; };

    RequestBuilder.prototype['orderByBuilderState'] = function (orderby) {
      let str = orderby
        .filter(function (sort) { return isPresent(sort.dir); })
        .map(function (sort) {
          let order = sort.field.replace(/\./g, '/');
          return sort.dir === 'desc' ? order + ' desc' : order;
        }).join(',');
      let result = str ? '$orderby=' + str : str;
      this.$orderBy = str;
      return this;
    };
    RequestBuilder.prototype['filterByBuilderState'] = function (filter) {
      this.$filter = serializeFilter(filter);
      return this;
    };
  }

  private createExpressions(filters: FilterDescriptor[]): ExpressionInterface[] {
    let expressions: Array<ExpressionInterface> = [];

    filters.forEach(filter => {
      let left = new PropertyExpression(filter.field);
      let right: ExpressionInterface = new ValueExpression(filter.value);
      let expression: ExpressionInterface;

      switch (filter.operator) {
        case 'contains':
          expression = new Contains(left, right);
          break;
        case 'doesnotcontain':
          let contains = new Contains(left, right);
          expression = new Not(contains);
          break;
        case 'endswith':
          expression = new EndsWith(left, right);
          break;
        case 'eq':
        case 'isnull':
        case 'isempty':
          if (filter.operator === 'isnull') {
            right = new PropertyExpression('null');
          }

          expression = new Equals(left, right);
          break;
        case 'gt':
          expression = new GreaterThan(left, right);
          break;
        case 'gte':
          expression = new GreaterThanOrEqualTo(left, right);
          break;
        case 'lt':
          expression = new LessThan(left, right);
          break;
        case 'lte':
          expression = new LessThanOrEqualTo(left, right);
          break;
        case 'neq':
        case 'isnotnull':
        case 'isnotempty':
          if (filter.operator === 'isnotnull') {
            right = new PropertyExpression('null');
          }

          expression = new NotEquals(left, right);
          break;
        case 'startswith':
          expression = new StartsWith(left, right);
          break;
      }

      expressions.push(expression);
    });

    return expressions;
  }

  public createRequest<T>(url: Table, state?: State): ODataRequest<T> {
    let requestBuilder = new ODataRequest<T>(this.http, this.baseUrl + Table[url]);
    if (state) {
      let odataString = toODataString(state);

      requestBuilder.addODataString(odataString);
      return requestBuilder;
    }
    requestBuilder.post;
    return requestBuilder;
  }

  public createTypedRequest<T>(c: { new(I): T }, url: Table, state?: State): ODataRequest<T> {
    let request = this.createRequest<T>(url, state);
    request.type = c;

    return request;
  }
}